import { supabase } from '../lib/supabase';
import {
  AdAccount,
  AdCampaign,
  AdCreative,
  AdMetrics,
  AdInvoice,
  ApiResponse,
  CreateAdAccountRequest,
  CreateCampaignRequest,
  CreateCreativeRequest,
  AnalyticsRequest,
  AnalyticsResponse
} from '../types/ads';

interface ImportMetaEnv {
  env: Record<string, string>;
}

class AdsApiService {
  // Ad Accounts
  async getAdAccounts(): Promise<AdAccount[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('ad_accounts')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async createAdAccount(accountData: CreateAdAccountRequest): Promise<AdAccount> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('ad_accounts')
      .insert({
        user_id: user.id,
        account_name: accountData.account_name,
        account_type: accountData.account_type || 'personal',
        business_name: accountData.business_name,
        sac_balance: 0, // Initial balance
        account_status: 'active'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async updateAdAccount(accountId: string, updates: Partial<AdAccount>): Promise<AdAccount> {
    const { data, error } = await supabase
      .from('ad_accounts')
      .update(updates)
      .eq('id', accountId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Campaigns
  async getCampaigns(accountId?: string): Promise<AdCampaign[]> {
    let query = supabase
      .from('ad_campaigns')
      .select('*')
      .order('created_at', { ascending: false });

    if (accountId) {
      query = query.eq('ad_account_id', accountId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  }

  async createCampaign(campaignData: CreateCampaignRequest): Promise<AdCampaign> {
    const { data, error } = await supabase
      .from('ad_campaigns')
      .insert({
        ad_account_id: campaignData.ad_account_id,
        name: campaignData.name,
        objective: campaignData.objective,
        budget_type: campaignData.budget_type || 'daily',
        budget_amount: campaignData.budget_amount,
        start_date: campaignData.start_date,
        end_date: campaignData.end_date,
        bid_strategy: campaignData.bid_strategy || 'lowest_cost',
        status: 'active'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async updateCampaign(campaignId: string, updates: Partial<AdCampaign>): Promise<AdCampaign> {
    const { data, error } = await supabase
      .from('ad_campaigns')
      .update(updates)
      .eq('id', campaignId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async deleteCampaign(campaignId: string): Promise<void> {
    const { error } = await supabase
      .from('ad_campaigns')
      .delete()
      .eq('id', campaignId);

    if (error) throw error;
  }

  // Creatives
  async getCreatives(accountId?: string): Promise<AdCreative[]> {
    let query = supabase
      .from('ad_creatives')
      .select('*')
      .order('created_at', { ascending: false });

    if (accountId) {
      query = query.eq('ad_account_id', accountId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  }

  async createCreative(creativeData: CreateCreativeRequest): Promise<AdCreative> {
    const { data, error } = await supabase
      .from('ad_creatives')
      .insert({
        ad_account_id: creativeData.ad_account_id,
        name: creativeData.name,
        type: creativeData.type,
        title: creativeData.title,
        body: creativeData.body,
        call_to_action: creativeData.call_to_action || 'learn_more',
        destination_url: creativeData.destination_url,
        display_url: creativeData.display_url,
        media_urls: creativeData.media_urls || [],
        thumbnail_url: creativeData.thumbnail_url,
        aspect_ratio: creativeData.aspect_ratio
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async updateCreative(creativeId: string, updates: Partial<AdCreative>): Promise<AdCreative> {
    const { data, error } = await supabase
      .from('ad_creatives')
      .update(updates)
      .eq('id', creativeId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async deleteCreative(creativeId: string): Promise<void> {
    const { error } = await supabase
      .from('ad_creatives')
      .delete()
      .eq('id', creativeId);

    if (error) throw error;
  }

  // Analytics
  async getAnalytics(params: AnalyticsRequest = {}): Promise<AnalyticsResponse> {
    let query = supabase
      .from('ad_metrics')
      .select('*');

    if (params.start_date) {
      query = query.gte('date', params.start_date);
    }
    if (params.end_date) {
      query = query.lte('date', params.end_date);
    }

    // Note: Filtering by account or campaign would require joins or separate queries
    // For simplicity, we'll fetch metrics and filter if needed, or rely on RLS

    const { data: metrics, error } = await query;

    if (error) throw error;

    // Aggregate metrics
    const summary = (metrics || []).reduce((acc, curr) => ({
      impressions: acc.impressions + (curr.impressions || 0),
      clicks: acc.clicks + (curr.clicks || 0),
      reach: Math.max(acc.reach, curr.reach || 0),
      spend: acc.spend + (curr.spend || 0),
      video_views: acc.video_views + (curr.video_views || 0),
      link_clicks: acc.link_clicks + (curr.link_clicks || 0),
      follows: acc.follows + (curr.follows || 0),
    }), {
      impressions: 0,
      clicks: 0,
      reach: 0,
      spend: 0,
      video_views: 0,
      link_clicks: 0,
      follows: 0
    });

    const average_ctr = summary.impressions > 0 ? (summary.clicks / summary.impressions) * 100 : 0;
    const average_cpc = summary.clicks > 0 ? summary.spend / summary.clicks : 0;

    return {
      metrics: metrics || [],
      summary: {
        ...summary,
        average_ctr,
        average_cpc,
        date_range: {
          start_date: params.start_date,
          end_date: params.end_date
        }
      }
    };
  }

  // Billing
  async getInvoices(_accountId?: string): Promise<AdInvoice[]> {
    void _accountId;
    // Anexo D.4.13 — Placeholder explícito: aún no existe tabla `ad_invoices` ni
    // integración facturación anuncios en Holded. Cuando se implemente, esta
    // función deberá hacer `supabase.from('ad_invoices').select(...)` y opcional-
    // mente sincronizar documentos con Holded. Devuelve [] intencionadamente.
    return [];
  }

  // Health check
  async healthCheck(): Promise<{ success: boolean; message: string; version: string }> {
    return { success: true, message: 'Direct DB Service Operational', version: '1.0.0' };
  }

  // Campaign management helpers
  async pauseCampaign(campaignId: string): Promise<AdCampaign> {
    return this.updateCampaign(campaignId, { status: 'paused' });
  }

  async activateCampaign(campaignId: string): Promise<AdCampaign> {
    return this.updateCampaign(campaignId, { status: 'active' });
  }

  async archiveCampaign(campaignId: string): Promise<AdCampaign> {
    return this.updateCampaign(campaignId, { status: 'archived' });
  }

  // Ad Tracking
  async trackAdEvent(adId: string, type: 'impression' | 'click' | 'follow'): Promise<ApiResponse<Record<string, unknown>>> {
    try {
      const { data, error } = await supabase.functions.invoke('track-ad-event', {
        headers: {
          apikey: (import.meta as unknown as ImportMetaEnv).env.VITE_SUPABASE_ANON_KEY,
        },
        body: {
          ad_id: adId,
          type,
          event_type: type,
          event: type,
          placement: 'feed',
        },
      });

      if (error) throw error;
      if (data?.success === false) {
        return { success: false, error: data?.error || 'track-ad-event failed' };
      }

      return { success: true, data };
    } catch (error) {
      console.error(`[adsApiService] Error tracking event ${type}:`, error);
      return { success: false, error: (error as Error).message };
    }
  }

  async recordAdTimeBlock(params: {
    adId: string;
    viewerKey: string;
    windowKey: number;
    placement?: string;
  }): Promise<ApiResponse<{ charged: boolean; new_balance?: number }>> {
    try {
      const { data, error } = await supabase.rpc('record_ad_time_block', {
        p_ad_id: params.adId,
        p_viewer_key: params.viewerKey,
        p_window_key: params.windowKey,
        p_placement: params.placement ?? 'feed',
      });

      if (error) throw error;

      const payload = (data ?? {}) as Record<string, unknown>;
      if (payload.success === false) {
        return { success: false, error: (payload.error as string) || 'record_ad_time_block failed' };
      }

      return {
        success: true,
        data: {
          charged: Boolean(payload.charged),
          new_balance: typeof payload.new_balance === 'number' ? payload.new_balance : undefined,
        },
      };
    } catch (error) {
      console.error('[adsApiService] Error recording time block:', error);
      return { success: false, error: (error as Error).message };
    }
  }

  // Budget management
  async getCampaignSpend(campaignId: string, startDate?: string, endDate?: string): Promise<number> {
    const analytics = await this.getAnalytics({
      campaign_id: campaignId, // Note: getAnalytics needs to support campaign_id filtering if we want this to be accurate
      start_date: startDate,
      end_date: endDate
    });

    return analytics.summary.spend;
  }

  // Performance metrics helpers
  async getCampaignPerformance(campaignId: string, dateRange: { start: string; end: string }) {
    const analytics = await this.getAnalytics({
      campaign_id: campaignId,
      start_date: dateRange.start,
      end_date: dateRange.end
    });

    const { summary } = analytics;

    return {
      impressions: summary.impressions,
      clicks: summary.clicks,
      reach: summary.reach,
      spend: summary.spend,
      ctr: summary.average_ctr,
      cpc: summary.average_cpc,
      cpm: summary.impressions > 0 ? (summary.spend / summary.impressions) * 1000 : 0,
      frequency: summary.reach > 0 ? summary.impressions / summary.reach : 0,
    };
  }

  // Export analytics data
  async exportAnalytics(params: AnalyticsRequest & { format?: 'csv' | 'json' }): Promise<Blob> {
    const analytics = await this.getAnalytics(params);

    if (params.format === 'csv') {
      const csvContent = this.convertToCSV(analytics.metrics);
      return new Blob([csvContent], { type: 'text/csv' });
    }

    return new Blob([JSON.stringify(analytics, null, 2)], { type: 'application/json' });
  }

  private convertToCSV(data: AdMetrics[]): string {
    if (data.length === 0) return '';

    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => Object.values(row).join(','));

    return [headers, ...rows].join('\n');
  }
}

export const adsApiService = new AdsApiService();
